import { Alert } from 'components/atoms/Alert';
import DatePicker from 'components/atoms/DatePicker/DatePicker.component';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Select } from 'components/atoms/Form/Select';
import NumberField from 'components/molecules/Fields/Number/Number';
import { Helper } from 'components/molecules/Helper';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useMemo, type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { type IUser } from 'types/users.types';
import { isNumeric } from 'utils/math';
import {
  currentYear,
  vehiclePurchaseDateMax,
  vehiclePurchaseDateMin,
} from '../../utils';
import {
  contractTypeOptions,
  fuelTypeOptions,
  markdownComponents,
  usageTypeOptions,
} from './helpers';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';

const VehicleFormFields: FC<{
  className?: string;
  warningsClassName?: string;
}> = ({ className, warningsClassName }) => {
  const { t } = useTranslation();
  const formApi = useFormContext();

  const formValues = formApi.watch();

  const { fuelType, usageType, co2Emission, weight, batteryCapacity } =
    formValues;

  const customer = useGetCurrentCustomerData().data as IUser;

  const shouldShowProfessionWarning =
    customer.APC1 &&
    usageType === 'professionalDriver' &&
    customer.APC1 !== 'drivers_and_courriers';

  const shouldShowFalseHybridWarning =
    fuelType === 'hybrid' &&
    ((isNumeric(co2Emission) && co2Emission > 50) ||
      (isNumeric(weight) &&
        isNumeric(batteryCapacity) &&
        batteryCapacity / weight < 0.005));

  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();

  const showChatBtn = useMemo(
    () =>
      shouldShowTaxAdvisor
        ? { showTaxAdvisorBtn: true }
        : { showIntercomBtn: true },
    [shouldShowTaxAdvisor],
  );

  return (
    <form className={className}>
      <FieldConnector
        name="name"
        label={t('expense.let_s_give_this_vehicle_a_name')}
      >
        {({ field }) => <FreeTextInput {...field} />}
      </FieldConnector>

      <FieldConnector
        name="fuelType"
        label={t('expense.what_kind_of_fuel_does_it_use')}
      >
        {({ field: { onChange, ...rest } }) => (
          <Select
            options={fuelTypeOptions}
            placeholder={t('invoice.field.select')}
            formatOptionLabel={(option: any) => t(option.label)}
            onChange={(option: any) => onChange(option.value)}
            {...rest}
          />
        )}
      </FieldConnector>

      <FieldConnector
        showField={
          !!(formValues.fuelType && formValues.fuelType !== 'electric')
        }
        name="co2Emission"
        label={`${t('expense.what_are_the_vehicle_s_emissions')}\n${t(
          'expense.you_can_find_this_on_its_registration_documents',
        )}`}
      >
        {({ field }) => (
          <NumberField
            placeholder={`100 ${t('expense.g_km')}`}
            format={{ prefix: ` ${t('expense.g_km')}` }}
            {...field}
          />
        )}
      </FieldConnector>

      {shouldShowFalseHybridWarning && (
        <Alert
          type="warning"
          {...showChatBtn}
          name="false hybrid vehicle"
          title={
            <div className="flex gap-4">
              <span>{t('vehicle.false_hybrid.warning.title')}</span>
              <span className="flex-shrink-0">
                <Helper>
                  <Helper.Paragraph>
                    <ReactMarkdown components={markdownComponents}>
                      {t('vehicle.false_hybrid.helper.text')}
                    </ReactMarkdown>
                  </Helper.Paragraph>
                </Helper>
              </span>
            </div>
          }
          description={t('vehicle.false_hybrid.warning.text')}
        />
      )}

      <FieldConnector
        showField={formValues.fuelType === 'hybrid'}
        name="weight"
        label={`${t('expense.weight_of_the_car')}\n${t(
          'expense.you_can_find_this_on_its_registration_documents',
        )}`}
      >
        {({ field }) => (
          <NumberField
            placeholder={`1000 ${t('expense.kg')}`}
            format={{ prefix: ` ${t('expense.kg')}` }}
            {...field}
          />
        )}
      </FieldConnector>

      <FieldConnector
        showField={formValues.fuelType === 'hybrid'}
        name="batteryCapacity"
        label={`${t('expense.capacity_of_battery')}\n${t(
          'expense.you_can_find_this_on_its_registration_documents',
        )}`}
      >
        {({ field }) => (
          <NumberField
            placeholder={`50 ${t('expense.kwh')}`}
            format={{ prefix: ` ${t('expense.kwh')}` }}
            {...field}
          />
        )}
      </FieldConnector>

      <FieldConnector
        showField={formValues.fuelType === 'CNG'}
        name="fiscalHorsepower"
        label={t('expense.what_is_the_vehicle_horsepower')}
      >
        {({ field }) => (
          <NumberField
            placeholder={`100 ${t('expense.horsepower')}`}
            format={{ prefix: ` ${t('expense.horsepower')}` }}
            {...field}
          />
        )}
      </FieldConnector>

      <FieldConnector
        name="registrationYear"
        ErrorContext={{ currentYear }}
        label={`${t(
          'expense.what_is_the_vehicle_s_first_year_of_registration',
        )}\n${t('expense.you_can_find_this_on_its_registration_documents')}`}
      >
        {({ field }) => (
          <FreeTextInput placeholder="2009" type="number" {...field} />
        )}
      </FieldConnector>

      <FieldConnector
        name="orderDate"
        label={t('expense.vehicle.order_date.label')}
      >
        {({ field: { value, onChange, ...field } }) => (
          <DatePicker
            {...field}
            filterDate={(date: Date) =>
              dayjs(date).isBetween(
                vehiclePurchaseDateMin,
                vehiclePurchaseDateMax,
                'day',
                '[]',
              )
            }
            selected={value ? dayjs(value + '').format('YYYY-MM-DD') : null}
            onChange={(date) => {
              const value = date ? +dayjs(date).format('YYYYMMDD') : null;
              onChange(value);
            }}
          />
        )}
      </FieldConnector>

      <FieldConnector
        name="usageType"
        label={t('expense.what_is_vehicle_deductibility')}
      >
        {({ field: { onChange, ...rest } }) => (
          <Select
            options={usageTypeOptions}
            placeholder={t('invoice.field.select')}
            onChange={(option: any) => onChange(option.value)}
            formatOptionLabel={(option: any, meta: any) => {
              if (meta.context === 'value') return t(option.label);
              return (
                <div>
                  <p className="text-sm font-semibold">{t(option.label)}</p>
                  {!!option.desc && <p className="text-sm">{t(option.desc)}</p>}
                </div>
              );
            }}
            {...rest}
          />
        )}
      </FieldConnector>

      {shouldShowProfessionWarning && (
        <Alert
          className={warningsClassName}
          type="warning"
          {...showChatBtn}
          description={t(
            'vehicle.driver_profession_deduct_percentage_warning',
            {
              APC2: t(`${customer.country}.APC2.${customer.APC2}`),
            },
          )}
        />
      )}

      <FieldConnector
        name="contractType"
        label={t('expense.what_kind_of_contract_do_you_have_on_this_vehicle')}
      >
        {({ field: { onChange, ...rest } }) => (
          <Select
            options={contractTypeOptions}
            placeholder={t('expense.i_own_it')}
            formatOptionLabel={(option: any) => t(option.label)}
            onChange={(option: any) => onChange(option.value)}
            {...rest}
          />
        )}
      </FieldConnector>
    </form>
  );
};

export default VehicleFormFields;
